/**
 * 六爻术语的最小白话对照表。
 *
 * 这些对照只解释原词，不引入趋势、结果或行动建议。
 * 单字的旺衰状态由 plain-language.js 在明确语境中替换，
 * 避免把「世应相合」中的「相」误当成旺衰状态。
 */

export const PLAIN_TERMS = Object.freeze({
  '用神': '代表爻',
  '原神': '帮助代表爻的力量',
  '忌神': '妨碍代表爻的力量',
  '仇神': '助长妨碍的力量',
  '忌神克用神': '妨碍的力量克制代表爻',
  '飞神克伏神': '上面的飞神压住藏在下面的代表爻，使它暂时出不来',
  '飞神生伏神': '上面的飞神帮助藏在下面的代表爻',
  '旺': '当令有力',
  '相': '得势',
  '休': '失时',
  '囚': '受制',
  '死': '无力',
  '月建': '当月的节气之气',
  '日辰': '当天的干支之气',
  '动爻': '正在变化的那一爻',
  '暗动': '表面不动但实际在起作用',
  '旬空': '暂时不在位',
  '月破': '被当月之气冲散',
  '日破': '被当日之气冲散',
  '入墓': '力量被暂时收住，暂时难以发挥',
  '入绝': '力量衰到最低，暂时难以发挥',
  '回头克': '变出的爻反过来克制原来的爻',
  '回头生': '变出的爻反过来生助原来的爻',
  '化进神': '变化后更进一步',
  '化退神': '变化后退回去',
  '六冲': '整体格局松散、易变',
  '六合': '整体格局紧密、稳定',
  '三刑': '多方面互相牵制',
  '伏神': '代表爻没有直接出现在卦面上，藏在另一爻下面',
  '飞神': '盖在藏起来的代表爻上面的那一爻',
  '世爻': '问卦的人自己',
  '应爻': '对方',

  // 现有 keyPoints 中已经出现的组合术语。
  '旺衰': '力量状态',
  '日墓': '在当日之气中被收藏',
  '化绝': '变到最弱的位置',
  '化空': '变化后暂时不在位',
  '合起': '原本不动的力量因相合而起作用',
  '合绊': '正在变化的力量被合住',
  '克处逢生': '在受制的同时也得到帮助',
  '空而不空': '虽然名义上暂时不在位，但已有力量使它仍可起作用',
  '临月建': '与当月的地支一样，得当月之气',
  '临日辰': '与当日地支相同',
  '月生': '得到当月之气的帮助',
  '日生': '得到当日之气的帮助',
  '月克': '受当月之气的制约',
  '日克': '受当日之气的制约',
  '月合': '与当月之气相合',
  '日合': '与当日之气相合',
  '月刑': '与当月之气互相牵制',
  '日刑': '与当日之气互相牵制',
  '月害': '受当月之气的妨害',
  '日害': '受当日之气的妨害',
  '日冲': '被当日之气冲动',
  '月冲': '被当月之气冲动',
  '静爻逢冲': '原本不变的爻被冲动',
  '十二长生': '衡量力量转变的十二个阶段',
  '动变': '这一爻变化后的情况',
  '卦体': '整个卦的排列',
  '断点': '观察的中心',
  '特殊状态': '额外状态',
  '候选时点': '按规则列出的日期',
  '冲开合绊': '用相冲的地支解开合住状态',
  '冲墓': '用相冲的地支解开收藏状态',
  '冲去克神': '用相冲的地支冲开妨碍代表爻的力量',
  '长生支': '力量开始生发所对应的地支',
  '世应相合': '问卦的人与对方的两爻相合'
});

/** 问事类别在白话里的具体称呼；键与 analyze.js 的 USE_GOD_MAP 对齐。 */
export const CATEGORY_FOCUS_LABELS = Object.freeze({
  求财: '钱财', 生意: '生意', 投资: '投资', 借贷: '借贷', 薪资: '收入', 失物: '所找的物品',
  事业: '工作', 工作: '工作', 升迁: '工作变动', 考公: '考公', 官司: '官司', 疾病: '病情',
  学业: '学业', 考试: '考试', 文书: '文书', 合同: '合同', 房产: '房产', 车辆: '车辆',
  父母: '父母', 长辈: '长辈', 天气: '天气',
  子女: '子女', 晚辈: '晚辈', 宠物: '宠物', 求医: '求医', 出行: '出行', 解忧: '所担心的事',
  兄弟: '兄弟姐妹', 朋友: '朋友', 同事: '同事', 合伙: '合作', 竞争: '竞争关系',
  婚姻男测: '对方', 婚姻女测: '对方',
  自身: '自己', 运势: '自身状态'
});

/** 十二长生各阶段只解释自身含义，不附加结果判断。 */
export const PLAIN_CHANGSHENG_STAGES = Object.freeze({
  长生: '处在开始生长的位置',
  沐浴: '刚接触外界，力量还不稳定',
  冠带: '逐渐成形，力量正在建立',
  临官: '已经成形，可以开始发挥',
  帝旺: '力量正处在最充足的时候',
  衰: '力量开始减弱',
  病: '力量已经明显减弱',
  死: '力量几乎不能发挥',
  墓: '力量被暂时收住，暂时难以发挥',
  绝: '力量衰到最低，暂时难以发挥',
  胎: '刚开始孕育，力量尚未显现',
  养: '正在积蓄，还未成形'
});

export const PLAIN_FORBIDDEN_WORDS = Object.freeze([
  '吉', '凶', '顺利', '不利', '能成', '不成', '可行', '不可行', '建议', '应该', '最好'
]);

/** 测试使用：这些词在白话文本里不应继续作为未解释行话出现。 */
export const REQUIRED_PLAIN_TERMS = Object.freeze([
  '用神', '原神', '忌神', '仇神', '月建', '日辰', '动爻', '暗动', '旬空',
  '月破', '日破', '入墓', '入绝', '回头克', '回头生', '化进神', '化退神',
  '六冲', '六合', '三刑', '伏神', '飞神', '世爻', '应爻', '旺衰', '日墓', '化绝',
  '化空', '合起', '合绊', '克处逢生', '空而不空', '临月建', '临日辰',
  '月生', '日生', '月克', '日克', '月合', '日合', '月刑', '日刑', '月害', '日害',
  '日冲', '月冲', '静爻逢冲', '十二长生', '动变', '卦体', '断点', '特殊状态',
  '候选时点', '冲开合绊', '冲墓', '冲去克神', '长生支', '世应相合',
  '忌神克用神', '飞神克伏神', '飞神生伏神'
]);
